import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TwoPaneLayout } from '../components/TwoPaneLayout';
import { StoreBadges } from '../components/StoreBadges';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { useAuth } from '../lib/auth';
import { consumeOAuthFragment, SESSION_EXPIRED_KEY } from '../lib/apiClient';
import PasswordField from '../components/PasswordField';

/**
 * Login page. Email + password form (Phase 3 hookup) plus a "Sign in
 * with Google" button (Phase 3.5).
 *
 * If the page is loaded with an OAuth callback fragment in the URL
 * (#oauth_result=success&access_token=…), we adopt the token and
 * navigate straight to the dashboard.
 */
export function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/dashboard';

  const { signIn, refresh, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Bounced here by a session timeout (a 401 cleared the token)? Read + clear
  // the flag once on mount so the notice shows exactly once.
  const [sessionTimedOut] = useState(() => {
    const v = sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return v;
  });

  // Process Google OAuth callback fragment on mount, if present.
  useEffect(() => {
    const result = consumeOAuthFragment();
    if (!result) return;
    if (result.ok && result.accessToken) {
      void (async () => {
        await refresh();
        navigate(next, { replace: true });
      })();
    } else if (!result.ok) {
      setOauthError(result.error ?? 'Sign-in failed. Please try again.');
    }
  }, [navigate, next, refresh]);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(next, { replace: true });
    } catch {
      // useAuth surfaces the error message; nothing to do here.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <TwoPaneLayout>
      <form onSubmit={onSubmit} noValidate>
        <h2 className="h4 mb-3">Sign in to AquaStar</h2>

        <div className="mb-3">
          <label className="form-label small text-muted">Email</label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Password</label>
          <PasswordField
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {sessionTimedOut && !authError && !oauthError && (
          <div className="alert alert-info" role="status">
            Your session timed out. Please sign in again.
          </div>
        )}

        {(authError || oauthError) && (
          <div className="alert alert-danger" role="alert">
            {oauthError || authError}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-100 mb-3"
          disabled={submitting || !email || !password}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        {/* OR divider — visual break before social-auth alternatives. */}
        <div className="d-flex align-items-center my-3" aria-hidden="true">
          <div className="flex-grow-1 border-top" />
          <span className="px-2 small text-muted">or</span>
          <div className="flex-grow-1 border-top" />
        </div>

        <GoogleSignInButton className="mb-3" />

        <div className="d-flex justify-content-between small">
          <Link to="/forgot-password">Forgot password?</Link>
          <Link to="/signup">Create an account</Link>
        </div>

        <hr className="my-4" />
        <p className="small text-muted text-center mb-2">
          Or get the AquaStar app:
        </p>
        <StoreBadges />
      </form>
    </TwoPaneLayout>
  );
}
